/**
 * CreateIntakeLinkDialog — modale de création d'un lien d'intake (Chunk 9.N.3.A).
 *
 * Pattern dialog maison (parité [FileUploadDialog](../../projects/_parts/FileUploadDialog.tsx)) —
 * pas de Headless UI / Radix : `role="dialog"` + `aria-modal="true"` + focus
 * trap + Escape + backdrop click. Refusé pendant la mutation pour éviter d'
 * abandonner un lien créé sans token affiché.
 *
 * Form fields (cadrage F2 §3.2 + Schemas.CreateIntakeLinkSchema) :
 *   - project_id        UUID requis (ProjectPicker)
 *   - expires_in_days   enum 7 | 30 (défaut) | 90 | null
 *   - max_submissions   int >= 1 optionnel (vide = illimité)
 *
 * Sécurité :
 *   - Le bundle réponse (intake_token + intake_url) **n'est PAS persisté ici** —
 *     il est levé via `onCreated(bundle)` au parent qui ouvre `IntakeTokenRevealDialog`.
 *   - Aucun `console.log` du bundle ou ses sous-champs.
 *   - Sur mutation error : afficher message bilingue + permettre retry, ne PAS
 *     fermer la modale (l'utilisateur risquerait de perdre sa saisie).
 */

import type { IntakeLinkBundle } from "@kollectivia/shared";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ProjectPicker } from "../../../components/ProjectPicker";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useCreateIntakeLink } from "../../../lib/queries/intakeLinks";

import { FormVersionSelector } from "./FormVersionSelector";

type ExpiresValue = "7" | "30" | "90" | "never";

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Levé après succès POST /v1/intake-links — le parent doit immédiatement
   * ouvrir le `IntakeTokenRevealDialog` avec ce bundle (token UNE FOIS).
   */
  onCreated: (bundle: IntakeLinkBundle) => void;
};

export function CreateIntakeLinkDialog({ open, onClose, onCreated }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const projectFieldId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const [projectId, setProjectId] = useState<string | undefined>();
  const [expires, setExpires] = useState<ExpiresValue>("30");
  const [maxSubsRaw, setMaxSubsRaw] = useState<string>("");
  const [formVersionId, setFormVersionId] = useState<string | undefined>();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const create = useCreateIntakeLink();
  const { reset: resetMutation } = create;

  // -- Reset à chaque (ré)ouverture --------------------------------------
  useEffect(() => {
    if (open) {
      setProjectId(undefined);
      setExpires("30");
      setMaxSubsRaw("");
      setFormVersionId(undefined);
      setSubmitAttempted(false);
      // Reset uniquement à la (ré)ouverture. Ne pas inclure `create` dans
      // les dep — l'objet useMutation change d'identité à chaque render
      // → boucle qui clear `error` aussitôt qu'il apparaît. Réf à la
      // fonction `reset` (stable) seulement.
      resetMutation();
      // Focus initial sur bouton fermer (parité FileUploadDialog) —
      // pour le screen reader, l'utilisateur Tab ensuite vers les champs.
      queueMicrotask(() => closeBtnRef.current?.focus());
    }
  }, [open, resetMutation]);

  // -- Escape ferme (sauf pendant mutation) ------------------------------
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, create.isPending]);

  // -- Validation locale -------------------------------------------------
  const projectInvalid = !projectId;
  const maxSubsTrim = maxSubsRaw.trim();
  const maxSubsParsed = maxSubsTrim === "" ? undefined : Number.parseInt(maxSubsTrim, 10);
  const maxSubsInvalid =
    maxSubsTrim !== "" && (!Number.isInteger(maxSubsParsed) || (maxSubsParsed as number) < 1);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setSubmitAttempted(true);
      if (projectInvalid || maxSubsInvalid) return;

      const expires_in_days =
        expires === "never" ? null : (Number.parseInt(expires, 10) as 7 | 30 | 90);

      create.mutate(
        {
          project_id: projectId!,
          expires_in_days,
          ...(maxSubsParsed === undefined ? {} : { max_submissions: maxSubsParsed }),
          ...(formVersionId === undefined ? {} : { form_template_version_id: formVersionId }),
        },
        {
          onSuccess: (bundle) => {
            // ⚠️ Anti-leak : bundle.intake_token NON loggé.
            onCreated(bundle);
          },
          // onError → géré par `create.error` ci-dessous (UI inline)
        },
      );
    },
    [
      create,
      expires,
      formVersionId,
      maxSubsInvalid,
      maxSubsParsed,
      onCreated,
      projectId,
      projectInvalid,
    ],
  );

  if (!open) return null;

  const apiErrorMsg =
    create.error instanceof ApiException
      ? getApiErrorMessage(create.error, lang) || t("errors.generic")
      : create.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!create.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-slate-900">
              {t("intake_links.create.title")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("intake_links.create.subtitle")}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label={t("common.close")}
            onClick={onClose}
            disabled={create.isPending}
            className="focus-visible:ring-brand-500 -mr-1 touch-manipulation rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <form onSubmit={onSubmit} noValidate className="space-y-5 px-5 py-5">
          {/* Project */}
          <div>
            <label
              htmlFor={projectFieldId}
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              {t("intake_links.create.project_label")}
              <span className="ml-1 text-red-600" aria-hidden="true">
                *
              </span>
            </label>
            <ProjectPicker
              id={projectFieldId}
              value={projectId}
              onChange={setProjectId}
              disabled={create.isPending}
            />
            {submitAttempted && projectInvalid && (
              <p className="mt-1 text-xs text-red-700" role="alert">
                {t("intake_links.create.errors.project_required")}
              </p>
            )}
          </div>

          {/* Expires */}
          <fieldset disabled={create.isPending}>
            <legend className="mb-1.5 text-sm font-medium text-slate-700">
              {t("intake_links.create.expires_label")}
            </legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(["7", "30", "90", "never"] as ExpiresValue[]).map((opt) => {
                const selected = expires === opt;
                return (
                  <label
                    key={opt}
                    className={[
                      "flex cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
                      "focus-within:ring-brand-500 focus-within:ring-2 focus-within:ring-offset-2",
                      "touch-manipulation",
                      selected
                        ? "border-brand-600 bg-brand-50 text-brand-700"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <input
                      type="radio"
                      name="expires"
                      value={opt}
                      checked={selected}
                      onChange={() => setExpires(opt)}
                      className="sr-only"
                    />
                    {t(`intake_links.create.expires_options.${opt}`)}
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* Max submissions */}
          <div>
            <label
              htmlFor="max-subs-input"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              {t("intake_links.create.max_submissions_label")}
              <span className="ml-1 text-xs font-normal text-slate-400">
                {t("intake_links.create.max_submissions_hint")}
              </span>
            </label>
            <input
              id="max-subs-input"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              className="input w-full"
              value={maxSubsRaw}
              onChange={(e) => setMaxSubsRaw(e.target.value)}
              disabled={create.isPending}
              aria-invalid={submitAttempted && maxSubsInvalid}
            />
            {submitAttempted && maxSubsInvalid && (
              <p className="mt-1 text-xs text-red-700" role="alert">
                {t("intake_links.create.errors.max_submissions_invalid")}
              </p>
            )}
          </div>

          {/* Formulaire versionné optionnel (9.U.5) — défaut = lien classique */}
          <FormVersionSelector
            value={formVersionId}
            onChange={setFormVersionId}
            disabled={create.isPending}
          />

          {/* API error */}
          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}

          <footer className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={create.isPending}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              aria-busy={create.isPending}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {create.isPending && (
                <svg
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    opacity="0.25"
                  />
                  <path
                    d="M14 8a6 6 0 0 1-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {create.isPending
                ? t("intake_links.create.submitting")
                : t("intake_links.create.submit")}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
